import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { map, mapTo, shareReplay, switchMap, take } from 'rxjs/operators';
import { SortInfo } from '../../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { mapToPaginatedResult } from '../../../overviews/generic-overview/components/pagination/helpers/map-to-paginated-result.helper';
import { SearchState } from '../../../overviews/search-page/entities/search-page-state.entity';
import { ApiQuery, Restriction } from '../../api/entities/api-query.entity';
import { ShApiCachingHelper } from '../../api/helpers/api-caching.helper';
import { ApiQueryBuilder } from '../../api/helpers/api-query-builder.helper';
import { CustomHttpParamEncoder } from '../../http/entities/custom-http-param-encoder';
import { ShAbstractHttpService } from '../../http/services/http.abstract.service';
import { ShPaginationService } from '../../http/services/pagination.service';
import { OrganisationId } from '../../organisations/entities/organisation.entity';
import { Operand } from '../../query/entities/operand.entity';
import { QueryData } from '../../query/entities/query-data.entity';
import { StructuredQuery } from '../../query/entities/structured-query.entity';
import { GenerateStructuredQueryFromAdvancedSearchTerm } from '../../query/helpers/structured-query.helper';
import { ZoneRestrictions } from '../../records/helpers/restrictions/record-zone-restrictions.helper';
import { PaginatedUser } from '../../users/entities/paginated-user.class';
import { User, UserId } from '../../users/entities/user.entity';
import { UserService } from '../../users/services/user.service';
import { CreateZone } from '../entities/create-zone.entity';
import { Zone, ZoneId } from '../entities/zone.entity';

@Injectable({ providedIn: 'root' })
export class ZonesService {
    protected readonly FALLBACK_VALUE: Zone[] = [];
    protected readonly TYPE_NAME: string = 'Zone';

    private readonly defaultHeaders: HttpHeaders = new HttpHeaders();
    private readonly userZoneEndpoint: string = `v2/users/current/zones`;
    private readonly zoneEndPoint: string = 'v2/zones';
    private readonly zoneRecordsEndpoint: string = 'v2/records';
    private userZones$: Observable<Zone[]>;

    constructor(
        private readonly http: ShAbstractHttpService,
        protected pagination: ShPaginationService,
        protected userService: UserService,
        private readonly apiCachingHelper: ShApiCachingHelper
    ) {
        this.initZones();
    }

    // #region init
    private initZones() {
        this.userZones$ = this.apiCachingHelper.cachedApiCall(
            this.pagination.getAll<Zone>(this.userZoneEndpoint, new HttpParams(), this.defaultHeaders, 500)
        );
    }

    // #region GET

    public getDefaultZone(contextZoneId?: ZoneId): Observable<Zone | null> {
        const userPreferencesZone$: Observable<ZoneId | null> = this.userService
            .getUserPreferences()
            .pipe(map(userPref => userPref.DefaultZone));

        return combineLatest([this.zones$, userPreferencesZone$]).pipe(
            map(([zones, preferredZoneId]) => this.defaultZoneSelector(zones, contextZoneId, preferredZoneId))
        );
    }

    public getZoneById(zoneId: ZoneId): Observable<Zone> {
        return this.findZoneInCache(zoneId).pipe(
            switchMap(zone => (zone ? of(zone) : this.http.get<Zone>(`${this.zoneEndPoint}/${zoneId}`)))
        );
    }

    public getZoneList(searchState: SearchState, organisationId?: string): Observable<PaginatedResults<Zone>> {
        const sortInfo: SortInfo = searchState.sortInfo;
        const paginationInfo: PaginationInfo = {
            page: searchState.page,
            resultsPerPage: searchState.pageSize
        };
        const startIndex: number = paginationInfo.page * paginationInfo.resultsPerPage;

        let params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('sort', sortInfo.field)
            .set('direction', sortInfo.direction);

        if (organisationId) {
            params = params.set('organisationId', organisationId);
        }

        searchState.structuredQuery.Fields?.forEach(queryPart => {
            if (queryPart.values.length > 0) {
                let wildcardSearch = false;
                const field: string = queryPart.field.replace('*', () => {
                    wildcardSearch = true;
                    return '';
                });

                let value: string = queryPart.values.join('');
                if (wildcardSearch) {
                    value = `*${value}*`;
                }

                params = params.set(field, value);
            }
        });

        return this.pagination
            .get<Zone>(this.zoneEndPoint, params, this.defaultHeaders, startIndex, paginationInfo.resultsPerPage)
            .pipe(map(results => mapToPaginatedResult(results, paginationInfo.resultsPerPage)));
    }

    public getZonesByOrganisationId(organisationId: OrganisationId): Observable<Zone[]> {
        const params: HttpParams = new HttpParams().set('organisationId', organisationId);
        return this.apiCachingHelper.cachedApiCall(
            this.pagination.getAll<Zone>(this.zoneEndPoint, params, this.defaultHeaders, 500)
        );
    }

    public getAllZoneUsers(zoneId: ZoneId): Observable<User[]> {
        const url = `${this.zoneEndPoint}/${zoneId}/users`;
        return this.apiCachingHelper.cachedApiCall(this.pagination.getAll<User>(url));
    }

    public getPaginatedZoneUsers(zoneId: ZoneId, queryData: QueryData): Observable<PaginatedResults<PaginatedUser>> {
        const sortInfo: SortInfo = queryData.sortInfo;
        const paginationInfo: PaginationInfo = queryData.paginationInfo;

        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('sort', sortInfo.field)
            .set('direction', sortInfo.direction);
        const startIndex: number = paginationInfo.page * paginationInfo.resultsPerPage;
        const url = `${this.zoneEndPoint}/${zoneId}/users`;

        return this.pagination
            .get<PaginatedUser>(url, params, this.defaultHeaders, startIndex, paginationInfo.resultsPerPage)
            .pipe(
                map(results => ({
                    ...results,
                    Results: results.Results.map(user => new PaginatedUser(user))
                }))
            )
            .pipe(map(results => mapToPaginatedResult(results, paginationInfo.resultsPerPage)));
    }

    public getUserCountByZoneId(zoneId: ZoneId): Observable<number> {
        const url = `${this.zoneEndPoint}/${zoneId}/users`;
        const params: HttpParams = new HttpParams();
        return this.apiCachingHelper
            .cachedApiCall(this.pagination.get(url, params, this.defaultHeaders, 0, 1))
            .pipe(map(result => result.TotalNrOfResults));
    }

    // #endregion

    // #region POST
    public createZone(zoneData: CreateZone): Observable<Zone> {
        return this.http.post<Zone>(this.zoneEndPoint, zoneData).pipe(take(1), shareReplay(1));
    }

    public addUsersToZone(zoneId: ZoneId, userIds: UserId[]): Observable<boolean[]> {
        if (userIds.length === 0) {
            return of([true]);
        }

        const requestStreams: Observable<boolean>[] = [];
        const request = `${this.zoneEndPoint}/${zoneId}/users`;
        // limited to 20 users per request
        for (let start = 0; start < userIds.length; start += 20) {
            requestStreams.push(this.http.post<void>(request, userIds.slice(start, start + 20)).pipe(mapTo(true)));
        }
        return combineLatest(requestStreams);
    }

    // #endregion

    // #region PUT

    public updateZone(zoneData: Zone): Observable<Zone> {
        const url = `${this.zoneEndPoint}/${zoneData.Id}`;
        return this.http.put<Zone>(url, zoneData).pipe(take(1), shareReplay(1));
    }

    // #endregion

    // #region DELETE

    public deleteZone(zoneId: ZoneId): Observable<boolean> {
        const url = `${this.zoneEndPoint}/${zoneId}`;
        return this.http.delete<boolean>(url).pipe(take(1), shareReplay(1));
    }

    public removeUsersFromZone(zoneId: ZoneId, userIds: UserId[]): Observable<boolean[]> {
        if (userIds.length === 0) {
            return of([true]);
        }

        const requestStreams: Observable<boolean>[] = [];
        const request = `${this.zoneEndPoint}/${zoneId}/users`;
        // limited to 20 users per request
        for (let start = 0; start < userIds.length; start += 20) {
            const userSubset: UserId[] = userIds.slice(start, start + 20);
            requestStreams.push(this.http.delete<boolean>(request, { body: userSubset }).pipe(mapTo(true)));
        }
        return combineLatest(requestStreams);
    }

    // #endregion

    // #region HEAD

    public getConceptCountByZoneId(zoneId: ZoneId): Observable<number> {
        const structuredQuery: StructuredQuery = GenerateStructuredQueryFromAdvancedSearchTerm(
            'ZoneId',
            [zoneId],
            Operand.Should
        );

        const restriction: Restriction = ZoneRestrictions.allRecordsInZone();

        const luceneQuery: string = ApiQueryBuilder.queryToLuceneQuery(new ApiQuery(structuredQuery, restriction));

        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('q', luceneQuery);
        return this.http.count(this.zoneRecordsEndpoint, params);
    }

    // #endregion

    // #region util

    private defaultZoneSelector(zones: Zone[], contextZoneId?: ZoneId, userPrefZoneId?: ZoneId | null): Zone | null {
        const contextZone: Zone | undefined = zones.find(zone => zone.Id === contextZoneId) ?? undefined;

        if (contextZone) {
            return contextZone;
        }

        const userPreferenceZone: Zone | undefined = zones.find(zone => zone.Id === userPrefZoneId) ?? undefined;

        if (userPreferenceZone) {
            return userPreferenceZone;
        }

        return zones.length >= 1 ? zones[0] : null;
    }

    private findZoneInCache(id: string): Observable<Zone | undefined> {
        return this.zones$ ? this.zones$.pipe(map(zones => zones.find(zone => zone.Id === id))) : of(undefined);
    }

    public get zones$(): Observable<Zone[]> {
        return this.userZones$.pipe(take(1));
    }

    // #endregion
}
